import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { LOGO_URL_SYMBOL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { Heart, LogOut, Crown, MessageCircle, Sparkles, Search, Menu, X, Shield, Sun, Moon } from "lucide-react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [unreadChats, setUnreadChats] = useState(0);

    const refreshUnread = useCallback(() => {
        if (!user) { setUnreadChats(0); return; }
        api.get("/chat/unread-count").then((r) => setUnreadChats(r.data.count || 0)).catch(() => {});
    }, [user]);

    useEffect(() => {
        refreshUnread();
        const it = setInterval(refreshUnread, 15000);
        window.addEventListener("chat-read", refreshUnread);
        window.addEventListener("focus", refreshUnread);
        return () => {
            clearInterval(it);
            window.removeEventListener("chat-read", refreshUnread);
            window.removeEventListener("focus", refreshUnread);
        };
    }, [refreshUnread]);

    useEffect(() => { refreshUnread(); }, [location.pathname, refreshUnread]);

    const navItems = [
        { to: "/annunci", label: "Cerca", icon: Search, testid: "nav-cerca" },
        { to: "/annunci", label: "Annunci", testid: "nav-annunci" },
        { to: "/prezzi", label: "Abbonamenti", testid: "nav-prezzi" },
        { to: "/venduti", label: "Venduti", testid: "nav-venduti" },
        { to: "/vendi-casa", label: "Valutazione Gratuita", testid: "nav-vendi-casa", highlight: true },
        { to: "/pubblica", label: "Pubblica", testid: "nav-pubblica" },
        ...(!user ? [{ to: "/login", label: "Accedi", testid: "nav-accedi" }] : []),
    ];

    return (
        <nav
            data-testid="navbar"
            className="fixed top-3 sm:top-4 left-0 right-0 z-50 px-6 sm:px-8"
        >
            <div className="navbar-pill max-w-[1500px] mx-auto bg-ink-950/85 backdrop-blur-2xl rounded-full px-5 sm:px-7 lg:px-10 h-16 sm:h-[4.25rem] flex items-center justify-between no-neu overflow-hidden">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0" onClick={() => setOpen(false)}>
                    <div data-testid="logo-badge-3d" className="logo-badge-3d">
                        <img
                            src={LOGO_URL_SYMBOL}
                            alt="VendiDirect"
                            className="logo-badge-3d-img"
                            draggable="false"
                            loading="eager"
                            decoding="async"
                        />
                    </div>
                    <span data-testid="brand-logo" className="brand-logo font-serif text-lg sm:text-xl text-ink-50 leading-none">vendi<span className="brand-logo-accent text-gold">direct</span>.it</span>
                </Link>

                {/* Desktop links */}
                <div className="hidden md:flex items-center gap-6 lg:gap-8 xl:gap-10 text-sm">
                    {navItems.map((n) => (
                        <Link
                            key={n.testid}
                            to={n.to}
                            data-testid={n.testid}
                            className={n.highlight
                                ? "navbar-pill-highlight inline-flex items-center gap-1.5 bg-gold text-white transition-colors duration-300 px-4 py-1.5 text-xs uppercase tracking-wider whitespace-nowrap rounded-full no-neu"
                                : "text-ink-100 hover:text-gold transition-colors duration-300 flex items-center gap-1.5 whitespace-nowrap"}
                        >
                            {n.icon && <n.icon size={14} />} {n.label}
                        </Link>
                    ))}
                </div>

                {/* Right actions */}
                <div className="flex items-center gap-2 sm:gap-3">
                    {!user && (
                        <Link
                            to="/onboarding"
                            data-testid="login-button"
                            className="hidden md:inline-flex cta-gold px-6 py-2.5 text-sm rounded-full no-neu"
                        >
                            Iscriviti gratis
                        </Link>
                    )}
                    <button
                        onClick={toggleTheme}
                        data-testid="theme-toggle"
                        aria-label={theme === "dark" ? "Passa a tema chiaro" : "Passa a tema scuro"}
                        title={theme === "dark" ? "Tema chiaro" : "Tema scuro"}
                        className="p-2 hover:text-gold transition-colors text-ink-100"
                    >
                        {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                    </button>
                    {user ? (
                        <>
                            {user.is_admin && (
                                <Link to="/admin" data-testid="nav-admin" className="p-2 hover:text-gold transition-colors text-gold" title="Area Proprietario">
                                    <Shield size={18} />
                                </Link>
                            )}
                            <Link to="/chat" data-testid="nav-chat" className="hidden sm:block relative p-2 hover:text-gold transition-colors text-ink-100" title="Chat">
                                <MessageCircle size={18} />
                                {unreadChats > 0 && (
                                    <span
                                        data-testid="chat-unread-badge"
                                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-gold text-ink-950 text-[10px] font-bold rounded-full leading-none"
                                    >
                                        {unreadChats > 9 ? "9+" : unreadChats}
                                    </span>
                                )}
                            </Link>
                            <Link to="/preferiti" data-testid="nav-preferiti" className="hidden sm:block p-2 hover:text-gold transition-colors text-ink-100" title="Preferiti">
                                <Heart size={18} />
                            </Link>
                            <Link to="/dashboard" data-testid="nav-dashboard" className="hidden sm:flex items-center gap-2 text-sm text-ink-100 hover:text-gold transition-colors">
                                {user.plan_active && (user.plan_promo ? <Sparkles size={14} className="text-gold" /> : <Crown size={14} className="text-gold" />)}
                                <span className="max-w-[140px] truncate">{user.name}</span>
                            </Link>
                            <button
                                onClick={async () => { await logout(); navigate("/"); }}
                                data-testid="logout-button"
                                className="p-2 hover:text-gold transition-colors text-ink-100"
                                title="Esci"
                            >
                                <LogOut size={18} />
                            </button>
                        </>
                    ) : null}

                    {/* Mobile hamburger */}
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="md:hidden p-2 text-ink-50 hover:text-gold"
                        aria-label="Menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile dropdown — pannello sospeso separato, coerente con la pillola */}
            {open && (
                <div data-testid="mobile-menu" className="md:hidden navbar-pill mt-2 bg-ink-950/95 backdrop-blur-2xl rounded-3xl no-neu">
                    <div className="px-4 py-4 flex flex-col">
                        {navItems.map((n) => (
                            <Link
                                key={n.testid}
                                to={n.to}
                                data-testid={`mobile-${n.testid}`}
                                onClick={() => setOpen(false)}
                                className={n.highlight
                                    ? "flex items-center gap-3 py-3 px-2 text-base text-gold hover:text-ink-50 border-b border-gold/40 transition-colors font-medium"
                                    : "flex items-center gap-3 py-3 px-2 text-base text-ink-100 hover:text-gold border-b border-white/5 transition-colors"}
                            >
                                {n.icon && <n.icon size={16} className="text-gold" />} {n.label}
                            </Link>
                        ))}
                        {!user && (
                            <Link
                                to="/onboarding"
                                data-testid="mobile-login"
                                onClick={() => setOpen(false)}
                                className="cta-gold mt-4 py-3 text-sm text-center rounded-full no-neu"
                            >
                                Iscriviti gratis
                            </Link>
                        )}
                        {user && (
                            <>
                                <Link to="/dashboard" data-testid="mobile-nav-dashboard" onClick={() => setOpen(false)} className="flex items-center gap-3 py-3 px-2 text-base text-ink-100 hover:text-gold border-b border-white/5 transition-colors">
                                    <Crown size={16} className="text-gold" /> Dashboard
                                </Link>
                                <Link to="/chat" data-testid="mobile-nav-chat" onClick={() => setOpen(false)} className="flex items-center gap-3 py-3 px-2 text-base text-ink-100 hover:text-gold border-b border-white/5 transition-colors">
                                    <MessageCircle size={16} className="text-gold" /> Chat
                                </Link>
                                <Link to="/preferiti" data-testid="mobile-nav-preferiti" onClick={() => setOpen(false)} className="flex items-center gap-3 py-3 px-2 text-base text-ink-100 hover:text-gold border-b border-white/5 transition-colors">
                                    <Heart size={16} className="text-gold" /> Preferiti
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
}
